import type { Round, Participant } from '@/types';
import { getSuitSymbol, getSuitColor } from './TrumpSelector';
import { cn } from '@/lib/utils';

interface RoundsTableProps {
  rounds: Round[];
  participants: Participant[];
  targetScore?: number;
}

export function RoundsTable({ rounds, participants, targetScore }: RoundsTableProps) {
  if (rounds.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-2">
        No rounds yet
      </p>
    );
  }

  // Calculate totals for footer
  const participantTotals = participants.map((participant) => {
    let totalRegular = 0;
    let totalDeclaration = 0;
    rounds.forEach((round) => {
      const score = round.scores.find((s) => s.participantId === participant.id);
      totalRegular += score?.regularPoints || 0;
      totalDeclaration += score?.declarationPoints || 0;
    });
    return {
      participantId: participant.id,
      total: totalRegular + totalDeclaration,
      totalRegular,
      totalDeclaration,
    };
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 px-1 text-xs text-muted-foreground font-medium w-8">
              #
            </th>
            {participants.map((p) => (
              <th
                key={p.id}
                className="text-center py-2 px-1 text-sm font-medium"
              >
                {p.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rounds.map((round) => {
            // Calculate round totals to determine winner
            const roundTotals = round.scores.map((score) => ({
              participantId: score.participantId,
              total: score.regularPoints + score.declarationPoints,
            }));
            const maxTotal = Math.max(...roundTotals.map((t) => t.total));
            const roundWinnerId = roundTotals.find(
              (t) => t.total === maxTotal && t.total > 0
            )?.participantId;

            return (
              <tr key={round.id} className="border-b last:border-0">
                <td className="py-2 px-1 text-sm text-muted-foreground text-center">
                  {round.roundNumber}
                </td>
                {participants.map((participant) => {
                  const score = round.scores.find(
                    (s) => s.participantId === participant.id
                  );
                  const regular = score?.regularPoints || 0;
                  const declaration = score?.declarationPoints || 0;
                  const total = regular + declaration;
                  const isWinner = participant.id === roundWinnerId;
                  const isTrumpCaller = participant.id === round.trumpCallerId;

                  return (
                    <td key={participant.id} className="py-2 px-1 text-center">
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-1">
                          <span
                            className={cn(
                              'text-base font-bold',
                              isWinner && 'text-green-600 dark:text-green-400'
                            )}
                          >
                            {total}
                          </span>
                          {isTrumpCaller && round.trumpSuit && (
                            <span className={cn('text-sm', getSuitColor(round.trumpSuit))}>
                              {getSuitSymbol(round.trumpSuit)}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {regular}
                          {declaration > 0 && (
                            <span className="text-primary">+{declaration}</span>
                          )}
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        {/* Totals row */}
        <tfoot>
          <tr className="border-t-2 bg-muted/50">
            <td className="py-3 px-1 text-xs text-muted-foreground font-medium">Σ</td>
            {participants.map((participant) => {
              const totalData = participantTotals.find(
                (t) => t.participantId === participant.id
              );
              const total = totalData?.total || 0;
              const totalRegular = totalData?.totalRegular || 0;
              const totalDeclaration = totalData?.totalDeclaration || 0;
              const remaining = targetScore ? Math.max(0, targetScore - total) : null;

              return (
                <td key={participant.id} className="py-3 px-1 text-center">
                  <div className="flex flex-col items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-2xl font-bold">{total}</span>
                      {remaining !== null && (
                        <span className="text-base text-muted-foreground">
                          ({remaining})
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {totalRegular}
                      {totalDeclaration > 0 && (
                        <span className="text-primary">+{totalDeclaration}</span>
                      )}
                    </span>
                  </div>
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
